import { BORDER_WIDTHS, Colors, Icon, Text } from '@amelio/ui-library';

import type { ReactNode } from 'react';

export interface PriorityContainerConfig {
    id: string;
    number: number;
    label: string;
    sublabel: string;
    markerColor?: string;
}

interface PriorityContainerProps {
    config: PriorityContainerConfig;
    isOver: boolean;
    disabled: boolean;
    dataTestId: string;
    onAddClick: (containerId: string) => void;
    children: ReactNode;
}

const PriorityContainer = ({
    config: { id: containerId, number, label, sublabel, markerColor = Colors.PRIMARY },
    isOver,
    disabled,
    dataTestId,
    onAddClick,
    children,
}: PriorityContainerProps) => {
    return (
        <div
            className={'flex h-full w-full flex-1 flex-col gap-4'}
            data-testid={`${dataTestId}-container-${containerId}`}
        >
            <div className={'flex items-center gap-1 px-1 sm:gap-2.5'}>
                <div
                    className={
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm'
                    }
                    style={{ backgroundColor: markerColor }}
                >
                    {number}
                </div>

                <div className={'flex items-center gap-1'}>
                    <Text
                        weight={'semibold'}
                        color={Colors.BASE}
                        className={'text-base'}
                    >
                        {label}
                    </Text>

                    <Text
                        weight={'regular'}
                        color={Colors.BASE_400}
                        className={'text-base'}
                    >
                        ({sublabel})
                    </Text>
                </div>
            </div>

            <div
                className={`flex h-full min-h-[340px] flex-1 flex-col gap-4 rounded-2xl p-7 max-md:p-4 sm:min-w-[250px] ${
                    isOver
                        ? `${BORDER_WIDTHS.md} border-primary bg-primary/[0.03]`
                        : `${BORDER_WIDTHS.sm} border-base-100 bg-white`
                }`}
            >
                <div className={'flex flex-1 flex-col'}>{children}</div>

                <div className={'flex justify-end'}>
                    <button
                        type={'button'}
                        onClick={() => onAddClick(containerId)}
                        className={
                            'bg-primary/10 hover:bg-primary/20 flex h-9 w-9 items-center justify-center rounded-lg transition-colors'
                        }
                        aria-label={`Add attribute to ${label}`}
                        data-testid={`${dataTestId}-add-button-${containerId}`}
                        disabled={disabled}
                    >
                        <Icon
                            name={'essential-add'}
                            size={'sm'}
                            color={Colors.PRIMARY}
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

export { PriorityContainer };
export type { PriorityContainerProps };
